/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import 'bootstrap/dist/css/bootstrap.min.css';
import Container from 'react-bootstrap/Container';
import { Row, Col } from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { StatusContext, StatusContextType } from './StatusContext';
import ProbeDetail from './ProbeDetail';
import { execControl, getCodeTemplates, getAiOps, queueCodeProbe, queueAiProbe, clearPendingProbes, clearAllProbes, aiRefresh, getProbe, getProbeChildren, getProbeRoots } from '../data';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { faCircleStop, faCirclePlay } from '@fortawesome/free-regular-svg-icons';
import { faForwardStep } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import QueueCodeProbeModal from './QueueCodeProbeModal';
import PurgeProbeModal from './PurgeProbeModal';
import { WsContext, WsContextType } from './WsContext';
import { LiveBar } from './LiveBar';
import { LivePlot } from './LivePlot';
import { LiveMap } from './LiveMap';
import { useTheme } from 'styled-components';
import QueueStatus from './QueueStatus';
import Dropdown from 'react-bootstrap/Dropdown';


function ExecPage({ }) {
    const [ liveProbe, setLiveProbe ] = useState({});   // displays in "Live Probe Detail"
    const [ showCodeModal, setShowCodeModal ] = useState(false);
    const [ showPurgeModal, setShowPurgeModal ] = useState(false);
    const [ pmStats, setPmStats ] = useState<any>({}); // probe manager stats
    const [ progressBar, setProgressBar ] = useState<any>({now:0,label:''});
    const [ progressBar2, setProgressBar2 ] = useState<any>({now:0,label:''});
    const [ barChart, setBarChart ] = useState<any>({});
    const [ lineChart, setLineChart ] = useState<any>({});
    const [ prompt, setPrompt ] = useState('');
    const [ reasoning, setReasoning ] = useState<string[]>([]);
    const [ response, setResponse ] = useState('');
    const statusContext = useContext(StatusContext) as StatusContextType;
    const wsContext = useContext(WsContext) as WsContextType;
    const [ logData, setLogData ] = useState([]);
    const logContainerRef : any = useRef(null);
    const respContainerRef : any = useRef(null);
    const [ execState, setExecState ] = useState('stopped');
    const theme : any = useTheme();
    const [ templates, setTemplates ] = useState<any>([]);
    const [ aiOps, setAiOps ] = useState<any[]>([]);
    const [ updateProbes, setUpdateProbes ] = useState(0);
    const [ lastActiveId, setLastActiveId ] = useState<number | null>(null);
    const [ hierarchyView, setHierarchyView ] = useState<'vis' | 'tree'>('vis');
    const liveProbeRef = useRef<any>({});
    const hierarchyViewRef = useRef<'vis' | 'tree'>('vis');
    const responseHasModelOutputRef = useRef(false);
    const responseLengthRef = useRef(0);

    useEffect(() => {
        liveProbeRef.current = liveProbe;
    }, [liveProbe]);

    useEffect(() => {
        hierarchyViewRef.current = hierarchyView;
    }, [hierarchyView]);

    const loadProbeTree = useCallback(async (rootId: number) => {
        try {
            const probe = await getProbe(rootId);
            const children = await getProbeChildren(rootId);
            setLiveProbe({ ...probe, children });
        } catch (e: any) {
            console.error('Error loading probe tree', rootId, e);
        }
    }, []);

    useEffect(() => {
        getProbeRoots().then((roots: any[]) => {
            if (roots.length > 0) {
                loadProbeTree(roots[0].id);
            }
        }).catch((e: any) => {
            console.error('Error loading initial probe root', e);
        });
    }, [loadProbeTree]);

    useEffect(() => {
        // Load templates from the server or a static file
        async function loadTemplates() {
            try {
                let rc = await getCodeTemplates();
                setTemplates(rc);
                rc = await getAiOps();
                setAiOps(rc);
            } catch (error) {
                console.error('Error loading templates:', error);
                setTemplates([]); // Fallback to default if loading fails
            }
        }
        loadTemplates();
    }, []);

    useEffect(() => {
        if (!wsContext.ws) {
            return;
        }
        // Register handlers
        if (wsContext.ws) {
            wsContext.ws.on('log', handlers.log);
            wsContext.ws.on('probeDetail', handlers.probeDetail);
            wsContext.ws.on('probeLifecycle', handlers.probeLifecycle);
            wsContext.ws.on('pmStats', handlers.pmStats);
            wsContext.ws.on('liveProgress', handlers.liveProgress);
            wsContext.ws.on('liveProgress2', handlers.liveProgress2);
            wsContext.ws.on('prompt', handlers.prompt);
            wsContext.ws.on('reasoning', handlers.reasoning);
            wsContext.ws.on('response', handlers.response);
            wsContext.ws.on('responseChunk', handlers.responseChunk);
            wsContext.ws.emit('refresh', {}); // get initial stats
        }
        
        // Clean up event listeners on unmount
        return () => {
            if (wsContext.ws) {
                wsContext.ws.off('log', handlers.log);
                wsContext.ws.off('probeDetail', handlers.probeDetail);
                wsContext.ws.off('probeLifecycle', handlers.probeLifecycle);
                wsContext.ws.off('pmStats', handlers.pmStats);
                wsContext.ws.off('liveProgress', handlers.liveProgress);
                wsContext.ws.off('liveProgress2', handlers.liveProgress2);
                wsContext.ws.off('prompt', handlers.prompt);
                wsContext.ws.off('reasoning', handlers.reasoning);
                wsContext.ws.off('response', handlers.response);
                wsContext.ws.off('responseChunk', handlers.responseChunk);
            }
        };
    } , [ wsContext, loadProbeTree ]);

    useEffect(() => {
        const el = logContainerRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [logData]);
    useEffect(() => {
        const el = respContainerRef.current;
        if (!el) {
            return;
        }
        const prevLen = responseLengthRef.current;
        const nextLen = response.length;
        responseLengthRef.current = nextLen;
        if (nextLen > prevLen) {
            el.scrollTop = el.scrollHeight;
        }
    }, [response]);

    function queueProbe(code : string) {
        queueCodeProbe(code).then((res) => {
            setUpdateProbes((prev) => prev + 1); // trigger re-fetch of probes
            console.log('Queued code probe, id=', res);
        }).catch((err) => {
            console.error('Error queueing code probe:', err);
            alert(`Error queueing code probe: ${err.message}`);
        });
    }

    // Store event handlers for later cleanup
    const handlers = {
        log: (msg: any) => {
            if (typeof msg === 'object') {
                msg = JSON.stringify(msg);
            } else if (typeof msg !== 'string') {
                msg = String(msg);
            } else {
                // string
                msg = msg.trim();
            }
            setLogData((prevLog : any) : any => { 
                prevLog.push(msg);
                return [...prevLog.slice(-100)];
            });
        },
        probeDetail: (msg: any) => {
            setLiveProbe(msg);
            const running = (msg.children || []).find((c: any) => c.running);
            if (running) {
                setLastActiveId(running.id);
            }
        },
        probeLifecycle: (msg: any) => {
            if (msg?.running && msg?.id) {
                setLastActiveId(msg.id);
            }
            const rootId = Number(msg?.rootId || msg?.id || 0);
            if (!rootId) {
                return;
            }
            const parentId = Number(msg?.parent_id || 0);
            const isRootLifecycle = !msg?.parent_id || parentId === 0;
            const currentRootId = Number(liveProbeRef.current?.probe_id || 0);
            if (!currentRootId || currentRootId === rootId || msg?.id === currentRootId || (msg?.running && isRootLifecycle)) {
                loadProbeTree(rootId);
            }
            if (isRootLifecycle) {
                if (msg.running) {
                    setHierarchyView('tree');
                } else if (currentRootId === rootId) {
                    setHierarchyView('vis');
                }
            }
        },
        pmStats: (msg: any) => {
            setPmStats(msg);
        },
        liveProgress: (msg: any) => {
            setProgressBar(msg);
        }, 
        liveProgress2: (msg: any) => {
            setProgressBar2(msg);
        }, 
        barChart: (msg: any) => {
            setBarChart(msg);
        },
        lineChart: (msg: any) => {
            setLineChart(msg);
        },
        prompt: (msg: any) => {
            setPrompt(msg);
            setReasoning([]);
            responseHasModelOutputRef.current = false;
            responseLengthRef.current = 0;
            setResponse('');
        },
        reasoning: (msg: any) => {
            const line = typeof msg === 'string' ? msg : JSON.stringify(msg, null, 2);
            setReasoning((prev) => [...prev.slice(-79), line]);
            if (!responseHasModelOutputRef.current) {
                setResponse((prev) => prev ? `${prev}\n[research] ${line}` : `[research] ${line}`);
            }
        },
        response: (msg: any) => {
            const nextText = typeof msg === 'string' ? msg : JSON.stringify(msg, null, 2);
            responseHasModelOutputRef.current = true;
            setResponse((prev) => prev === nextText ? prev : nextText);
        },
        responseChunk: (msg: any) => {
            const chunk = String(msg || '');
            if (!chunk) {
                return;
            }
            if (!responseHasModelOutputRef.current) {
                responseHasModelOutputRef.current = true;
                setResponse((prev) => prev === chunk ? prev : chunk);
                return;
            }
            setResponse((prev) => {
                let r = prev + chunk
                return r;
            });
        }
    };

    const doExecControl = (cmd: string) => {
        async function doExec() {
            try {
                const res = await execControl(cmd);
            } catch (error : any) {
                console.error(`Error executing command ${cmd}:`, error);
                statusContext.postError(`Error executing command ${cmd}: ${error.message}`);
            }
        }
        doExec();
    }

    const fullBar = progressBar.now >= 100;
    const fullBar2 = progressBar2.now >= 100;

    return (
        <>
        <Container fluid style={{ backgroundColor: theme.colors.bg, color: theme.colors.text, height: '100%', width: '100%' }}>
            <Row className="ps-2" style={{ height: '100%' }}>
                <Col md={6} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Row style={{ height: '3em', marginTop: '.5rem', paddingBottom: '.5rem' }} >
                        <ButtonToolbar className="align-items-center" aria-label="Run Controls">
                            <Col md={7}>
                                <ButtonGroup className="me-2" aria-label="queue">
                                    <Dropdown as={ButtonGroup} className="me-2">
                                        <Dropdown.Toggle variant="success" id="ai-ops-dropdown">
                                            AI Ops
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {aiOps.sort((a,b) => a.id.localeCompare(b.id))
                                                   .map((op: any, index: number) => (
                                                <Dropdown.Item 
                                                    key={index} 
                                                    onClick={() => queueAiProbe(op.id)}
                                                >
                                                    {op.id}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </ButtonGroup>
                                <ButtonGroup className="me-2" aria-label="queue">
                                    <Dropdown as={ButtonGroup} className="me-2">
                                        <Dropdown.Toggle variant="success" id="net-ops-dropdown">
                                            Net Ops
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item key="dns" onClick={() => { console.log("dns");}}>DNS Probe</Dropdown.Item>
                                            <Dropdown.Item key="tcp" onClick={() => { console.log("tcp");}}>TCP Probe</Dropdown.Item>
                                            <Dropdown.Item key="tls" onClick={() => { console.log("tls");}}>TLS/HTTP</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </ButtonGroup>
                                <ButtonGroup className="me-2" aria-label="queue">
                                    <Dropdown as={ButtonGroup} className="me-2">
                                        <Dropdown.Toggle variant="warning" id="template-dropdown">
                                            Code
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            {templates.map((template: any, index: number) => (
                                                <Dropdown.Item 
                                                    key={index} 
                                                    onClick={() => queueProbe(template.template)}
                                                >
                                                    {template.description}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </ButtonGroup>
                                <ButtonGroup className="me-2" aria-label="queue">
                                    <Dropdown as={ButtonGroup} className="me-2">
                                        <Dropdown.Toggle variant="warning" id="tools-dropdown">
                                            Tools
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            <Dropdown.Item 
                                                key={1} 
                                                onClick={() => {
                                                    clearPendingProbes().catch((err) => {
                                                        statusContext.postError(`Error clearing pending probes: ${err.message}`);
                                                    });
                                                }}
                                            >
                                            Clear all pending probes
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                key={2} 
                                                onClick={() => {
                                                    clearAllProbes().catch((err) => {
                                                        statusContext.postError(`Error clearing all probes: ${err.message}`);
                                                    });
                                                }}
                                            >
                                            Clear ALL probes
                                            </Dropdown.Item>
                                            <Dropdown.Item 
                                                key={3} 
                                                onClick={() => {
                                                    aiRefresh().catch((err) => {
                                                        statusContext.postError(`Error refreshing AI templates: ${err.message}`);
                                                    });
                                                }}
                                            >
                                            Refresh AI templates
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </ButtonGroup>
                            </Col>
                            <Col md={5} className="text-end">
                                <span className="me-3 font-monospace" style={{ color: theme.colors.ctext }}>&nbsp;Run</span>
                                <ButtonGroup className="me-2" aria-label="run">
                                    <Button variant="success" onClick={() => doExecControl('next')}>
                                        <FontAwesomeIcon icon={faForwardStep} className="me-2" /> Next
                                    </Button>
                                    <Button variant="primary" onClick={() => doExecControl('all')}>
                                        <FontAwesomeIcon icon={faCirclePlay} className="me-2" />
                                        All
                                    </Button>
                                    <Button variant="danger" onClick={() => doExecControl('stop')}>
                                        <FontAwesomeIcon icon={faCircleStop} className="me-2" />
                                        Stop
                                    </Button>
                                </ButtonGroup>
                            </Col>
                        </ButtonToolbar>
                    </Row>
                    <Row style={{ height: '3em', paddingBottom: '.5rem' }} className="border-bottom">
                        <Col>
                            <Button className="me-1" variant="primary" onClick={() => queueAiProbe('test1')}>
                                T1
                            </Button>
                            <Button className="me-1" variant="primary" onClick={() => queueAiProbe('AAA_test2')}>
                                T2
                            </Button>
                            <Button className="me-1" variant="primary" onClick={() => queueAiProbe('update_finger_ws')}>
                                UPF
                            </Button>
                            <Button className="me-1" variant="primary" onClick={() => queueAiProbe('research_finger')}>
                                RESF
                            </Button>
                            <Button className="me-1" variant="primary"
                                onClick={() => {
                                    clearAllProbes().catch((err) => {
                                        statusContext.postError(`Error clearing all probes: ${err.message}`);
                                    });
                                }} >
                                ClearProbes
                            </Button>
                            <Button variant="primary"
                                onClick={() => {
                                    aiRefresh().catch((err) => {
                                        statusContext.postError(`Error refreshing AI templates: ${err.message}`);
                                    });
                                }}>
                                AiRefresh
                            </Button>
                        </Col>
                    </Row>
                    <Row className="" style={{ backgroundColor: theme.colors.bg, color: theme.colors.text, flex: 1, minHeight: 0 }}>
                        <Col style={{ overflowY: 'auto', height: '100%' }}>
                                <ProbeDetail
                                    probe={liveProbe}
                                    hierarchyView={hierarchyView}
                                    lastActiveId={lastActiveId}
                                    showHierarchyToggle={true}
                                    onToggleHierarchyView={() => setHierarchyView(prev => prev === 'vis' ? 'tree' : 'vis')}
                                />
                        </Col>
                    </Row>
                </Col>
                <Col md={6} className="border" style={{ backgroundColor: theme.colors.mapBg, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Row className="pt-2 pb-2 border-bottom" style={{ height: '3em', backgroundColor: theme.colors.mapBg, color: theme.colors.ctext, flexShrink: 0 }}>
                        <Col className="border-end">
                            <ProgressBar className="h-100 w-100"
                                style={{ backgroundColor: theme.colors.progressBg, color: theme.colors.progressFg }}
                                striped={!fullBar} animated={!fullBar}
                                now={progressBar.now}
                                label={progressBar.label}
                            />
                        </Col>
                        <Col>
                            <ProgressBar className="h-100 w-100"
                                style={{ backgroundColor: theme.colors.progressBg, color: theme.colors.progressFg }}
                                striped={!fullBar2} animated={!fullBar2}
                                now={progressBar2.now}
                                label={progressBar2.label}
                            />
                        </Col>
                    </Row>
                    <Row className="pb-1 border-bottom" style={{ backgroundColor: theme.colors.queueBg, flexShrink: 0 }}>
                        <Col className="pb-1">
                            <QueueStatus pmStats={pmStats} />
                        </Col>
                    </Row>
                        <Row style={{ height: '20em', flexShrink: 0 }}>
                            <LiveMap geoFill={theme.colors.mapFill} geoStroke={theme.colors.mapStroke} geoBg={theme.colors.mapBg} />
                    </Row>
                        <Row style={{ height: '11em', flexShrink: 0 }}>
                        { (prompt !== '' || response != '' || reasoning.length > 0) ? (
                            <>
                            <Col className="border-end border-top border-bottom h-100 overflow-auto"
                             style={{ 
                                overflowY: 'auto',
                                backgroundColor: theme.colors.logBg,
                                color: theme.colors.promptTextColor,
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.8em'
                            }}>
                                {prompt}
                            </Col>
                            <Col className="border-end h-100 border-top border-bottom overflow-auto"
                             style={{ 
                                overflowY: 'auto',
                                fontFamily: 'monospace',
                                backgroundColor: theme.colors.logBg,
                                color: theme.colors.ctext,
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.8em'
                            }}>
                                {reasoning.join('\n')}
                            </Col>
                            <Col className="h-100 border-top border-bottom overflow-auto"
                             ref={respContainerRef}
                             style={{ 
                                overflowY: 'auto',
                                fontFamily: 'monospace',
                                backgroundColor: theme.colors.logBg,
                                color: theme.colors.responseTextColor,
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.8em'
                            }}>
                                {response}
                            </Col>
                            </>
                        ) : (
                            <>
                            <Col className="border-end border-top border-bottom h-100 overflow-hidden justify-content-center align-items-center" style={{ background: theme.colors.mapBg }}>
                                <LiveBar />
                            </Col>
                            <Col className="h-100 border-top border-bottom overflow-hidden justify-content-center align-items-center" style={{ background: theme.colors.mapBg }}>
                                <LivePlot />
                            </Col>
                            </>
                        )}
                    </Row>
                    <Row style={{ flex: 1, minHeight: 0 }}>
                        <div ref={logContainerRef} style={{ 
                            height: '100%',
                            width: '100%',
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            backgroundColor: theme.colors.logBg,
                            color: theme.colors.ctext,
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.8em'
                        }}>
                            {logData.join('\n')}
                        </div>
                    </Row>
                </Col>
            </Row>
        </Container>
        { showCodeModal &&
            <QueueCodeProbeModal show={showCodeModal} setShow={setShowCodeModal} />
        }
        { showPurgeModal &&
            <PurgeProbeModal show={showPurgeModal} setShow={setShowPurgeModal} />
        }
        </>
    );
}
export default ExecPage;